/*
 * Copyright (C) 2025 to Present Applause App Quality, Inc. All rights reserved.
 */

import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Simplified ulinky pipe that converts URLs, hashtags, and mentions to clickable links
 * Note: This is a simplified version of the AngularJS ulinky filter
 * Does not include full LinkExtractor integration or emoji processing
 */
@Pipe({
  name: 'ulinky',
  standalone: false,
})
export class UlinkyPipe implements PipeTransform {
  // Regex patterns
  private readonly URL_REGEXP = /(?:^|[^@A-Za-z0-9._%+-])((ftp|https?):\/\/|(www\.)|(mailto:)?[A-Za-z0-9._%+-/*]+@)\S*[^\s.;,(){}<>"\u201d\u2019]/gi;
  private readonly HASHTAG_REGEXP = /\B[#]+[A-Za-z0-9-_]*[a-zA-Z]+[A-Za-z0-9-_]*/g;
  private readonly MENTION_REGEXP = /(?:[\W]|^)(@([a-zA-Z0-9_\-@\.]+))/g;
  private readonly MAILTO_REGEXP = /(^|\s)mailto:/i;

  constructor(private sanitizer: DomSanitizer) {}

  transform(text: string | null | undefined): SafeHtml {
    if (!text) {
      return '';
    }

    let html = text;

    // Handle hashtags - convert to search links
    html = html.replace(this.HASHTAG_REGEXP, (match) => {
      const hashtag = match.substring(1); // Remove the # symbol
      const encodedHashtag = encodeURIComponent(hashtag);
      return `<a class="internal" href="#!/search?contentType=all&query=${encodedHashtag}">${match}</a>`;
    });

    // Handle mentions - convert to profile links
    html = html.replace(this.MENTION_REGEXP, (match, fullMatch, username) => {
      const whitespace = match[0] === ' ' ? ' ' : '';
      if (username) {
        return `${whitespace}<a class="internal" href="#!/profile/${username}">${fullMatch}</a>`;
      }
      return match;
    });

    // Handle regular URLs
    html = html.replace(this.URL_REGEXP, (match) => {
      let url = match.trim();
      const whitespace = match[0] === ' ' ? ' ' : '';

      // Skip if starts with parenthesis (likely part of markdown)
      if (url[0] === '(') {
        return match;
      }

      // If no protocol, add http:// or mailto:
      if (!url.match(/^(ftp|https?):\/\//)) {
        if (url.includes('@') && !url.match(/^mailto:/)) {
          url = `mailto:${url}`;
        } else if (url.match(/^www\./)) {
          url = `http://${url}`;
        }
      }

      // Remove mailto: from display text
      const displayText = match.replace(this.MAILTO_REGEXP, '').trim();

      return `${whitespace}<a href="${url}" target="_blank" rel="noopener noreferrer">${displayText}</a>`;
    });

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
